import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Pencil } from "lucide-react";
import { toast } from "sonner";
import { PAYMENT_METHODS, type ApiError, type PaymentMethod, type WorkPayment } from "../api";
import { cn } from "../lib/utils";
import { Button } from "./ui/button";
import { Dialog } from "./ui/dialog";
import { MoneyInput } from "./money-input";
import { MultiSelectDropdown } from "./ui/multi-select-dropdown";
import { PaymentMethodSelect, usePaymentMethodText } from "./payment-method-select";

/**
 * Hujjatning to'lov usuli — to'lov tafsiloti yonida ko'rsatiladi.
 *
 * `onCorrect` berilsa qiymatning O'ZI tugmaga aylanadi: egasi usulni aynan
 * o'zi qaragan joyda to'g'rilaydi, amallar ustuniga to'rtinchi ikonka
 * qo'shilmaydi (u yerda "qalam" allaqachon TO'LOV QABUL QILISH degani —
 * ikkalasini yonma-yon qo'yish ularni chalkashtirardi).
 *
 * Yozilmagan usul "Noma'lum" bo'lib ko'rinadi — bo'sh joy ham, chiziqcha ham
 * emas (`usePaymentMethodText`).
 */
export function PaymentMethodValue({
  usul,
  payments,
  onCorrect,
  className,
}: {
  usul: PaymentMethod | null | undefined;
  payments?: WorkPayment[];
  /** Berilmasa — faqat o'qish uchun (masalan bekor qilingan sotuv). */
  onCorrect?: () => void;
  className?: string;
}) {
  const { t } = useTranslation("common");
  const text = usePaymentMethodText();
  const value = payments?.length
    ? payments.map((payment) => text(payment.tolov_usuli)).join(", ")
    : text(usul);
  // The existing correction endpoint is intentionally single-method only.
  const canCorrect = !!onCorrect && (payments?.length ?? 0) <= 1;
  return (
    <span className={cn("inline-flex items-center gap-1", className)}>
      <span className="text-muted-foreground">{t("paymentMethod.label")}:</span>{" "}
      {canCorrect ? (
        <button
          type="button"
          // Ish jurnalida butun qator tafsilotni ochib-yopadi — bosish
          // yuqoriga o'tib ketmasligi kerak.
          onClick={(e) => {
            e.stopPropagation();
            onCorrect();
          }}
          title={t("paymentMethod.correct.action")}
          aria-label={t("paymentMethod.correct.action")}
          className="inline-flex items-center gap-1 font-medium underline decoration-dotted underline-offset-4 transition-colors hover:text-primary"
        >
          {value}
          <Pencil aria-hidden className="size-3 opacity-70" />
        </button>
      ) : (
        <span className="font-medium">{value}</span>
      )}
    </span>
  );
}

/** Work documents can carry several independently classified paid amounts. */
export function WorkPaymentValue({
  payments,
  onCorrect,
  className,
}: {
  payments: WorkPayment[];
  onCorrect?: () => void;
  className?: string;
}) {
  const { t } = useTranslation("common");
  const text = usePaymentMethodText();
  const value = payments.length
    ? payments.map((payment) => `${text(payment.tolov_usuli)} · ${payment.summa}`).join(", ")
    : text(null);
  return (
    <span className={cn("inline-flex items-center gap-1", className)}>
      <span className="text-muted-foreground">{t("paymentMethod.label")}:</span>{" "}
      {onCorrect ? (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onCorrect();
          }}
          title={t("paymentMethod.correct.action")}
          aria-label={t("paymentMethod.correct.action")}
          className="inline-flex items-center gap-1 font-medium underline decoration-dotted underline-offset-4 transition-colors hover:text-primary"
        >
          {value}
          <Pencil aria-hidden className="size-3 opacity-70" />
        </button>
      ) : (
        <span className="font-medium">{value}</span>
      )}
    </span>
  );
}

type CorrectionLine = { method: PaymentMethod; amount: string };

/**
 * Split-aware work correction. The document's aggregate paid amount is fixed;
 * only its method allocation changes, so debt/payment state cannot move.
 */
export function WorkPaymentCorrectionDialog({
  open,
  onOpenChange,
  current,
  totalPaid,
  partyLabel,
  onSave,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  current: WorkPayment[];
  totalPaid: string;
  partyLabel?: string;
  onSave: (payments: Array<{ tolov_usuli: PaymentMethod; summa: string }>) => Promise<void>;
}) {
  const { t } = useTranslation("common");
  const known = current.flatMap((payment) => payment.tolov_usuli
    ? [{ method: payment.tolov_usuli, amount: String(Number(payment.summa)) }]
    : []);
  const [lines, setLines] = useState<CorrectionLine[]>(known);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const paid = Number(totalPaid) || 0;
  const allocated = lines.reduce((sum, line) => sum + (Number(line.amount) || 0), 0);
  const invalid = lines.some((line) => !line.amount || Number(line.amount) <= 0)
    || (lines.length > 0 && allocated !== paid);
  const initialSignature = known.map((line) => `${line.method}:${Number(line.amount)}`).join("|");
  const signature = lines.map((line) => `${line.method}:${Number(line.amount)}`).join("|");

  function toggle(method: PaymentMethod) {
    setLines((existing) => {
      if (existing.some((line) => line.method === method)) {
        return existing.filter((line) => line.method !== method);
      }
      return [...existing, { method, amount: existing.length === 0 ? String(paid) : "" }];
    });
  }

  async function submitSplit() {
    if (invalid) {
      setErr(t("paymentMethod.correct.splitTotal", { total: totalPaid }));
      return;
    }
    setErr(null);
    setBusy(true);
    try {
      await onSave(lines.map((line) => ({
        tolov_usuli: line.method,
        summa: String(Number(line.amount)),
      })));
      toast.success(t("paymentMethod.correct.saved"));
      onOpenChange(false);
    } catch (e) {
      const ae = e as ApiError;
      setErr(ae?.message || t("common.error"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !busy && onOpenChange(next)} contentClassName="max-w-sm overflow-hidden">
      <div className="flex max-h-[calc(100dvh-2rem)] flex-col">
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-5">
          <div>
            <h2 className="pr-8 text-lg font-semibold leading-none tracking-tight">
              {t("paymentMethod.correct.title")}
            </h2>
            {partyLabel && <p className="mt-1.5 text-sm text-muted-foreground">{partyLabel}</p>}
          </div>
          <p className="rounded-md border bg-muted/30 px-3 py-2.5 text-sm text-muted-foreground">
            {t("paymentMethod.correct.hint")}
          </p>
          <div className="flex items-baseline justify-between gap-2 text-sm">
            <span className="text-xs uppercase tracking-wider text-muted-foreground">
              {t("paymentMethod.correct.fixedTotal")}
            </span>
            <span className="font-medium tabular-nums">{totalPaid}</span>
          </div>
          <MultiSelectDropdown
            id="work-payment-correction"
            open={pickerOpen}
            onOpenChange={setPickerOpen}
            disabled={busy || paid <= 0}
            placeholder={t("paymentMethod.correct.selectMethods")}
            selected={lines}
            options={PAYMENT_METHODS}
            selectedKey={(line) => line.method}
            optionKey={(method) => method}
            optionLabel={(method) => t(`paymentMethod.${method}`)}
            onOptionToggle={toggle}
            renderSelected={(line) => (
              <div key={line.method} className="flex items-center justify-between gap-2 rounded px-2 py-1.5 text-sm">
                <span>{t(`paymentMethod.${line.method}`)}</span>
                <Button type="button" variant="ghost" size="sm" onClick={() => toggle(line.method)} disabled={busy}>
                  {t("actions.delete")}
                </Button>
              </div>
            )}
          />
          {lines.map((line) => (
            <div key={line.method} className="space-y-1">
              <label htmlFor={`correct-${line.method}`} className="text-xs text-muted-foreground">
                {t("paymentMethod.correct.amountFor", { method: t(`paymentMethod.${line.method}`) })}
              </label>
              <MoneyInput
                id={`correct-${line.method}`}
                value={line.amount}
                onChange={(amount) => setLines((existing) => existing.map((item) =>
                  item.method === line.method ? { ...item, amount } : item
                ))}
                disabled={busy}
              />
            </div>
          ))}
          {err && (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {err}
            </div>
          )}
        </div>
        <div className="flex shrink-0 gap-2 border-t bg-background p-5">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            {t("actions.cancel")}
          </Button>
          <Button type="button" onClick={submitSplit} disabled={busy || invalid || signature === initialSignature} className="flex-1">
            {busy ? t("actions.processing") : t("paymentMethod.correct.save")}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

/**
 * To'lov usulini TO'G'RILASH dialogi — pul harakat qilmaydi.
 *
 * Bu "to'lov qabul qilish" EMAS (u `PaymentEditDialog`): bu yerda summa
 * maydoni ham, qarz ko'rsatkichi ham ATAYLAB yo'q va matn ham buni ochiq
 * aytadi. Egasi ikkisini chalkashtirsa, kelmagan pulni yozib qo'ydim deb
 * o'ylab qolardi.
 *
 * "— tanlanmagan —" ni tanlash usulni TOZALAYDI (server ochiq `null` ni
 * shunday tushunadi). Ota komponent dialogni shartli mount qiladi — mount ==
 * ochilish, shuning uchun boshlang'ich qiymat `current` dan bir marta olinadi.
 */
export function PaymentMethodCorrectionDialog({
  open,
  onOpenChange,
  current,
  partyLabel,
  onSave,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  current: PaymentMethod | null;
  partyLabel?: string;
  onSave: (usul: PaymentMethod | null) => Promise<void>;
}) {
  const { t } = useTranslation("common");
  const text = usePaymentMethodText();
  const [usul, setUsul] = useState<PaymentMethod | "">(current ?? "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const next = usul || null;
  const unchanged = next === current;

  async function submit() {
    setErr(null);
    setBusy(true);
    try {
      // Ko'rinadigan qiymat FAQAT server tasdiqlagach yangilanadi — bu yerda
      // hech narsa oldindan o'zgartirilmaydi.
      await onSave(next);
      toast.success(t("paymentMethod.correct.saved"));
      onOpenChange(false);
    } catch (e) {
      const ae = e as ApiError;
      // `tolov_usuli_without_payment` serverning uzbekcha lug'atida YO'Q —
      // simdan umumiy "Noto'g'ri ma'lumot" keladi va egasiga hech narsa
      // aytmaydi, shuning uchun kodni o'zimiz o'qiymiz. Tekshiruv mijozda
      // TAKRORLANMAYDI: so'rov baribir yuboriladi, qarorni server chiqaradi.
      setErr(
        ae?.code === "tolov_usuli_without_payment"
          ? t("paymentMethod.correct.noPayment")
          : ae?.message || t("common.error"),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => !busy && onOpenChange(o)}
      contentClassName="max-w-sm"
    >
      <div className="space-y-4 p-5">
        <div>
          <h2 className="pr-8 text-lg font-semibold leading-none tracking-tight">
            {t("paymentMethod.correct.title")}
          </h2>
          {partyLabel && (
            <p className="mt-1.5 text-sm text-muted-foreground">{partyLabel}</p>
          )}
        </div>

        <p className="rounded-md border bg-muted/30 px-3 py-2.5 text-sm text-muted-foreground">
          {t("paymentMethod.correct.hint")}
        </p>

        <div className="flex items-baseline justify-between gap-2 text-sm">
          <span className="text-xs uppercase tracking-wider text-muted-foreground">
            {t("paymentMethod.correct.current")}
          </span>
          <span className="font-medium">{text(current)}</span>
        </div>

        <PaymentMethodSelect
          id="usul-correct"
          value={usul}
          onChange={setUsul}
          disabled={busy}
          className="space-y-1"
        />

        {err && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {err}
          </div>
        )}

        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            className="flex-none"
          >
            {t("actions.cancel")}
          </Button>
          <Button
            type="button"
            onClick={submit}
            disabled={busy || unchanged}
            className="flex-1"
          >
            {busy ? t("actions.processing") : t("paymentMethod.correct.save")}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
